import React from 'react';
import { Spinner, Button, Badge } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { showLocalWhen, showUtcWhen } from '../../utils/utcDateTime';
import DOMPurify from 'dompurify';
import './shopHourModal.css';

const ShopHourList = ({ hours, isLoading, onDelete, onEdit, readOnly = false }) => {
  const isShopHourActive = (startTime, endTime) => {
    // Get current time in UTC
    const now = DateTime.utc();

    // Parse the UTC times from the API
    const start = DateTime.fromISO(startTime, { zone: 'utc' });
    const end = DateTime.fromISO(endTime, { zone: 'utc' });

    return now >= start && now <= end;
  };

  if (isLoading && !hours.length) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!isLoading && !hours.length) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No shop hours found</p>
      </div>
    );
  }

  return (
    <div className="container">
      <table className="table table-striped">
        <thead>
          <tr>
            <th>Shop Name</th>
            <th>Description</th>
            <th>Start Time (UTC)</th>
            <th>End Time (UTC)</th>
            <th>Start Time (Local)</th>
            <th>End Time (Local)</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="7" className="text-center py-4">
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </td>
            </tr>
          ) : (
            hours.map(hour => {
              const sanitizedDescription = DOMPurify.sanitize(hour.description || '');
              const descriptionText = sanitizedDescription
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
              const truncatedDescription =
                descriptionText.length > 120
                  ? `${descriptionText.slice(0, 120)}...`
                  : descriptionText;
              return (
                <tr key={hour.id}>
                  <td>{hour.name}</td>
                  <td>
                    <div className="description-content" title={descriptionText}>
                      {truncatedDescription || '—'}
                    </div>
                  </td>
                  <td>{showUtcWhen(hour.startTime)}</td>
                  <td>{showUtcWhen(hour.endTime)}</td>
                  <td>{showLocalWhen(hour.startTime)}</td>
                  <td>{showLocalWhen(hour.endTime)}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {isShopHourActive(hour.startTime, hour.endTime) && (
                        <Badge bg="success" className="px-3 py-2">
                          RUNNING
                        </Badge>
                      )}
                      {!readOnly && (
                        <>
                          <Button variant="primary" size="sm" onClick={() => onEdit(hour)}>
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() =>
                              onDelete(hour.id, isShopHourActive(hour.startTime, hour.endTime))
                            }
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ShopHourList;
